import {
  Button,
  Input,
  Form,
  InputMask,
  ModalFooter,
  errorToast,
} from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { LoadingBlurDiv } from "@shared/components";
import { useLocalization } from "@i18n";

import { createAdminUserSchema, AdminUserFormData } from "../../schemas";
import { useAdminUserQuery } from "../../api/admin-user";

interface EditAdminUserFormProps {
  onSubmit: (data: AdminUserFormData) => void;
  onCancel: () => void;
  userId?: string;
}

export const EditAdminUserForm = ({
  onSubmit,
  onCancel,
  userId,
}: EditAdminUserFormProps) => {
  const { translate } = useLocalization();

  const {
    data: userData,
    isLoading,
    error,
    isError,
  } = useAdminUserQuery(userId ?? "", {
    enabled: !!userId,
  });

  if (isError) {
    const errorAxios = error as AxiosError;
    errorToast({
      description:
        errorAxios.parsedMessage ?? translate("common.errorEditing"),
    });
    onCancel();
  }

  const form = useForm<AdminUserFormData>({
    resolver: zodResolver(
      createAdminUserSchema(translate, {
        includeUserTypeField: false,
        forceTellusEmailMode: false,
      }),
    ),
    values: {
      name: userData?.name ?? "",
      email: userData?.email ?? "",
      phone: userData?.phone ?? "",
      department: userData?.department ?? "",
      position: userData?.position ?? "",
    },
  });

  return (
    <LoadingBlurDiv isLoading={isLoading}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="px-[32px] py-[16px]">
            <div className="grid grid-cols-2 gap-4">
              <Input
                control={form.control}
                label={translate("admin.users.form.label.name")}
                name="name"
                type="text"
                placeholder={translate("admin.users.form.placeholder.name")}
              />
              <Input
                control={form.control}
                label={translate("admin.users.form.label.email")}
                name="email"
                type="email"
                placeholder={translate("admin.users.form.placeholder.email")}
                disabled
              />
              <InputMask
                control={form.control}
                label={translate("admin.users.form.label.phone")}
                name="phone"
                type="text"
                mask="(99) 99999-9999"
              />
              <Input
                control={form.control}
                label={translate("admin.users.form.label.department")}
                name="department"
                type="text"
                placeholder={translate(
                  "admin.users.form.placeholder.department",
                )}
              />
              <Input
                control={form.control}
                label={translate("admin.users.form.label.position")}
                name="position"
                type="text"
                placeholder={translate(
                  "admin.users.form.placeholder.position",
                )}
              />
            </div>
          </div>
          <ModalFooter
            footer={
              <Button type="submit" variant="primary">
                {translate("admin.users.button.save")}
              </Button>
            }
            closeModal={
              <Button type="button" variant="secondary" onClick={onCancel}>
                {translate("admin.users.button.cancel")}
              </Button>
            }
          />
        </form>
      </Form>
    </LoadingBlurDiv>
  );
};
